import { formatContactAsyncOption } from '../../../api/contacts/formatters'
import contacts from '../../../api/contacts/hooks'
import i18n from '../../../i18n'
import { baseAsyncSelectInput, baseCheckboxInput, baseDateInput, baseSelectInput, baseTextInput } from '../fieldTypes'
import { documentDetailSearchFields, documentFields } from './fields'

export const documentFieldTypes = (disableIdFields = false) => {
    return {
        [documentFields.FILE_DESCRIPTION]: {
            ...baseTextInput,
            name: documentFields.FILE_DESCRIPTION,
            label: i18n.t('table.header.documents.fileName'),
            required: true,
            colProps: {
                sm: 12,
            },
        },
        [documentFields.DOCUMENT_PROJECT_INTERNAL_APP_ID]: {
            ...baseTextInput,
            name: documentFields.DOCUMENT_PROJECT_INTERNAL_APP_ID,
            label: i18n.t('app.common.projectId'),
            tooltip: i18n.t('form.documentFormSidebar.document.projectId.tooltip'),
            handleInputDisable: (formik) =>
                disableIdFields ||
                !!formik.values[documentFields.DOCUMENT_PROPERTY_INTERNAL_APP_ID] ||
                !!formik.values[documentFields.DOCUMENT_CONTACT_ID],
            colProps: {
                sm: 6,
            },
        },
        [documentFields.DOCUMENT_PROPERTY_INTERNAL_APP_ID]: {
            ...baseTextInput,
            name: documentFields.DOCUMENT_PROPERTY_INTERNAL_APP_ID,
            label: i18n.t('app.common.propertyId'),
            tooltip: i18n.t('form.documentFormSidebar.document.propertyId.tooltip'),
            handleInputDisable: (formik) =>
                disableIdFields ||
                !!formik.values[documentFields.DOCUMENT_PROJECT_INTERNAL_APP_ID] ||
                !!formik.values[documentFields.DOCUMENT_CONTACT_ID],
            colProps: {
                sm: 6,
            },
        },
        [documentFields.DOCUMENT_CONTACT_ID]: {
            ...baseAsyncSelectInput,
            name: documentFields.DOCUMENT_CONTACT_ID,
            label: i18n.t('form.project.documents.contact'),
            searchCriteria: ['name'],
            tooltip: i18n.t('form.project.documents.contact.tooltip'),
            noLimit: true,
            handleInputDisable: (formik) =>
                disableIdFields ||
                !!formik.values[documentFields.DOCUMENT_PROJECT_INTERNAL_APP_ID] ||
                !!formik.values[documentFields.DOCUMENT_PROPERTY_INTERNAL_APP_ID],
            colProps: {
                sm: 12,
            },
            useEntityHook: contacts.GetContacts.useQuery,
            formatOption: formatContactAsyncOption,
            handleChange: (e, formik, fieldName) => {
                formik.setValues({
                    ...formik.values,
                    [fieldName]: e?.value || '',
                    [documentFields.DOCUMENT_CONTACT_INTERNAL_APP_ID]: e?.data?.internalAppId ?? '',
                })
            },
        },
        [documentFields.DOCUMENT_TYPE]: {
            ...baseSelectInput,
            name: documentFields.DOCUMENT_TYPE,
            label: i18n.t('app.common.documentType'),
            required: true,
            colProps: {
                sm: 12,
            },
        },
        [documentFields.DOCUMENT_CREATION_DATE]: {
            ...baseDateInput,
            name: documentFields.DOCUMENT_CREATION_DATE,
            label: i18n.t('app.common.creationDate'),
            colProps: {
                sm: 6,
            },
        },
        [documentFields.DOCUMENT_VALIDITY_DATE]: {
            ...baseDateInput,
            name: documentFields.DOCUMENT_VALIDITY_DATE,
            label: i18n.t('app.common.expirationDate'),
            colProps: {
                sm: 6,
            },
        },
        [documentFields.FILE_SYNC]: {
            ...baseCheckboxInput,
            name: documentFields.FILE_SYNC,
            label: i18n.t('form.property.map.sync'),
            tooltip: i18n.t('form.property.map.sync.tooltip'),
            colProps: {
                sm: 12,
            },
        },
    }
}

export const documentDetailSearchFieldTypes = () => {
    return {
        [documentDetailSearchFields.FILE_DESCRIPTION]: {
            ...baseTextInput,
            name: documentDetailSearchFields.FILE_DESCRIPTION,
            label: i18n.t('table.header.documents.fileName'),
            colProps: { sm: 12 },
        },
        [documentDetailSearchFields.DOCUMENT_PROJECT_ID]: {
            ...baseTextInput,
            name: documentDetailSearchFields.DOCUMENT_PROJECT_ID,
            label: i18n.t('app.common.projectId'),
            colProps: { sm: 6 },
            handleInputDisable: (formik) =>
                !!formik.values[documentDetailSearchFields.DOCUMENT_CONTACT_ID] || !!formik.values[documentDetailSearchFields.DOCUMENT_PROPERTY_ID],
        },
        [documentDetailSearchFields.DOCUMENT_PROPERTY_ID]: {
            ...baseTextInput,
            name: documentDetailSearchFields.DOCUMENT_PROPERTY_ID,
            label: i18n.t('app.common.propertyId'),
            colProps: { sm: 6 },
            handleInputDisable: (formik) =>
                !!formik.values[documentDetailSearchFields.DOCUMENT_CONTACT_ID] || !!formik.values[documentDetailSearchFields.DOCUMENT_PROJECT_ID],
        },
        [documentDetailSearchFields.DOCUMENT_CONTACT_ID]: {
            ...baseAsyncSelectInput,
            name: documentDetailSearchFields.DOCUMENT_CONTACT_ID,
            label: i18n.t('form.project.documents.contact'),
            searchCriteria: ['name'],
            noLimit: true,
            storeFullOption: true,
            colProps: { sm: 12 },
            useEntityHook: contacts.GetContacts.useQuery,
            formatOption: formatContactAsyncOption,
            handleInputDisable: (formik) =>
                !!formik.values[documentDetailSearchFields.DOCUMENT_PROJECT_ID] || !!formik.values[documentDetailSearchFields.DOCUMENT_PROPERTY_ID],
        },
        [documentDetailSearchFields.DOCUMENT_TYPE]: {
            ...baseSelectInput,
            name: documentDetailSearchFields.DOCUMENT_TYPE,
            label: i18n.t('app.common.documentType'),
            colProps: { sm: 12 },
        },
        [documentDetailSearchFields.DOCUMENT_CREATION_DATE_FROM]: {
            ...baseDateInput,
            name: documentDetailSearchFields.DOCUMENT_CREATION_DATE_FROM,
            placeholder: i18n.t('app.common.from'),
            colProps: { sm: 6, className: 'pe-0' },
        },
        [documentDetailSearchFields.DOCUMENT_CREATION_DATE_TO]: {
            ...baseDateInput,
            name: documentDetailSearchFields.DOCUMENT_CREATION_DATE_TO,
            placeholder: i18n.t('app.common.to'),
            colProps: { sm: 6, className: 'pe-0' },
        },
        [documentDetailSearchFields.DOCUMENT_VALIDITY_DATE_FROM]: {
            ...baseDateInput,
            name: documentDetailSearchFields.DOCUMENT_VALIDITY_DATE_FROM,
            placeholder: i18n.t('app.common.from'),
            colProps: { sm: 6, className: 'pe-0' },
        },
        [documentDetailSearchFields.DOCUMENT_VALIDITY_DATE_TO]: {
            ...baseDateInput,
            name: documentDetailSearchFields.DOCUMENT_VALIDITY_DATE_TO,
            placeholder: i18n.t('app.common.to'),
            colProps: { sm: 6, className: 'pe-0' },
        },
    }
}
